// Angular
import { Observable, Subscription, takeUntil } from 'rxjs';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Models
import { AppCleanup, LookUpResponse, TenantDto, UserModel } from '../../shared/models';
import { AuthService } from '../../modules/auth';
import { ConfirmDialogModel } from '../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Services
import { UserService } from '../../modules/user/services/user.service';
import { CoreService } from '../../shared/services';
import { DialogService } from '../../shared/services/dialog.service';
import { SpinnerService } from '../../shared/components/spinner/services/spinner.service';

// Libraries

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';

// Helpers
import { FormHelper, RegexHelper, UtilityHelper } from '../../shared/helpers';
import { TranslateService } from '@ngx-translate/core';

// Store
import { AuthActions } from '../../modules/auth/state/auth.action';
import { UserAuthActions } from '../../modules/auth/state/user-auth.action';
import { LookupState } from '../../shared/state/lookup.state';

// Enums
import { Roles } from '../../modules/auth/models/roles.enum';
import { StatusType } from '../../shared/enums';

import { SpinnerSizeType } from '../../shared/components/spinner/spinner.component';
@Component({
	selector: 'user-edit-dialog',
	templateUrl: './user-edit-dialog.component.html',
	styleUrls: ['./user-edit-dialog.component.scss'],
})
export class UserEditSimpleDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	readonly SpinnerSizeType = SpinnerSizeType;
	componentTitle: string = 'User profile';
	@Select(LookupState.lookups) lookups$: Observable<LookUpResponse>;
	editModel: UserModel;
	form: FormGroup;
	tenants: TenantDto[] = [];
	isTenantAdmin: boolean = false;
	tenantChanged: boolean = false; // Needed to trigger the hard page refresh after tenant change
	showSpinner: boolean = true;
	trimSubscription: Subscription;
	currentUserPrimaryRole: string;

	constructor(
		@Inject(MAT_DIALOG_DATA) private data: any,
		private formBuilder: FormBuilder,
		private userService: UserService,
		private coreService: CoreService,
		private store: Store,
		private dialogRef: MatDialogRef<UserEditSimpleDialogComponent>,
		private authService: AuthService,
		private translateService: TranslateService,
		private spinnerService: SpinnerService,
		private dialogService: DialogService
	) {
		super();
	}

	ngOnInit() {
		const id = this.data.id;
		if (id && id > 0) {
			this.getUser(id);
		}

		this.lookups$.pipe(takeUntil(this.destroy)).subscribe((lookups) => {
			if (lookups) {
				this.tenants = lookups.tenants;
			}
		});

		this.authService
			.checkAccess([Roles.TenantAdmin], () => {
				return true;
			})
			.subscribe((isTenantAdmin) => {
				this.isTenantAdmin = isTenantAdmin;
			});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	private createForm() {
		const NO_WHITESPACE = FormHelper.CUSTOM_VALIDATORS.NO_WHITESPACE;

		this.form = this.formBuilder.group({
			firstName: [this.editModel.firstName ?? '', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100), NO_WHITESPACE])],
			lastName: [this.editModel.lastName ?? '', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100), NO_WHITESPACE])],
			email: [this.editModel.email ?? '', Validators.compose([Validators.required, Validators.email, Validators.minLength(3), Validators.maxLength(320)])],
			phone: [this.editModel.phone ?? '', Validators.compose([Validators.pattern(RegexHelper.phoneNumber())])],
			username: [this.editModel.username ?? '', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100), NO_WHITESPACE])],
			role: [this.currentUserPrimaryRole],
			tenantId: [this.editModel.tenantId ?? '', Validators.compose([Validators.required, Validators.maxLength(100)])],
		});

		setTimeout(() => {
			this.form?.controls['email'].disable();
			this.form?.controls['role'].disable();
		}, 1);
	}

	getUser(id: number) {
		this.userService
			.getUser(id)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (user: UserModel) => {
					if (user) {
						this.editModel = user;
						this.currentUserPrimaryRole = this.userService.getUserPrimaryRole(user.roles);
						this.showSpinner = false;
						this.createForm();
					}
				},
				error: () => {
					this.showSpinner = false;
				},
			});
	}

	submit() {
		if (!this.form.dirty) return;
		const originalUsername: string = this.editModel.username;
		const updatedUsername: string = this.form.controls.username.value.trim();

		// If the user changes their Username, prompt with confirmation modal
		// and log out the user after the user changes successfully save.
		// Otherwise, update the user without the prompt.
		if (originalUsername !== updatedUsername) {
			this.displayUsernameChangeConfirmDialog();
		} else {
			this.processUserUpdate();
		}
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}

	getDisableSubmit() {
		return !this.form?.valid || !this.form.dirty;
	}

	private displayUsernameChangeConfirmDialog() {
		const confirmModel: ConfirmDialogModel = {
			title: 'Update username',
			message: 'Updating the Username will log you out of the system and prompt you to log back in with your new credentials. Are you sure you want to proceed?',
			action: this.translateService.instant('ACTION.UPDATE_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.processUserUpdate(true);
				}
			});
	}

	private processUserUpdate(shouldLogOutUser: boolean = false) {
		const controls = this.form.controls;

		if (this.form.invalid) {
			Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
			return;
		}

		this.prepareModel();
		this.updateModel();

		if (shouldLogOutUser) {
			this.store.dispatch(new AuthActions.DeleteToken());
			this.store.dispatch(new UserAuthActions.DeleteUser());
		}
	}

	private prepareModel() {
		if (this.editModel.roles.includes(2) || this.editModel.roles.includes(3)) {
			this.editModel.companyIds = [];
		}

		const controls = this.form.controls;
		this.editModel.firstName = controls.firstName.value.trim();
		this.editModel.lastName = controls.lastName.value.trim();
		this.editModel.phone = controls.phone.value.trim();
		this.editModel.username = controls.username.value.trim();
		this.editModel.tenantId = controls.tenantId.value;
	}

	private updateModel() {
		if (this.editModel.id) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			this.userService
				.saveUserSimple(this.editModel)
				.pipe(takeUntil(this.destroy))
				.subscribe((user) => {
					this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					const message = `${this.translateService.instant('MODULE.USER.INFO_UPDATED')}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					this.store.dispatch(new UserAuthActions.UpdateUser(user));
					this.dialogRef.close(true);
				});
		}
	}
}
